import type { PaletteEntry } from './fieldMeta';

/**
 * Drag-and-drop vocabulary for the form-builder canvas.
 *
 * Drag is an ENHANCEMENT layered over the existing arm-and-place clicking, not
 * a replacement for it. Two reasons, both load-bearing:
 *
 *  1. WCAG 2.1.1 (Keyboard, Level A). A drag-only interaction is unreachable
 *     without a pointer. Click-to-arm then click-to-place is the keyboard path
 *     and has to keep working — the builder is an Originator tool covered by
 *     the SFB-13 / SFA-40 accessibility scope.
 *  2. Drag was removed from this canvas once already (f8eb21b, 2026-06-07)
 *     because hover tooltips obscured drop targets mid-drag. Keeping the click
 *     path means re-introducing drag is additive: if it misbehaves again, it
 *     can be switched off without leaving the originator unable to build a form.
 */

/**
 * Palette entries that can be dropped into a column slot.
 *
 * Compounds (the Address preset) are deliberately excluded: they create a whole
 * new SECTION with several rows rather than filling one column, so there is no
 * slot for them to target. They stay click-only, which is also why the drag
 * payload can rely on `defaultLabelEn` existing.
 */
export type DraggablePaletteEntry = Extract<PaletteEntry, { kind: 'field' | 'customField' }>;

/** What a drag is carrying. Discriminated so `onDragEnd` can dispatch without
 *  guessing from id shapes. */
export type BuilderDragData =
  | { kind: 'palette'; entry: DraggablePaletteEntry }
  | { kind: 'placedField'; sectionId: string; rowId: string; columnId: string };

/** What a drop target represents — always a single column slot. */
export type BuilderDropData = {
  sectionId: string;
  rowId: string;
  columnId: string;
  /** True when the slot already holds a field. Occupied slots are not valid
   *  targets: the move endpoint places into an EMPTY column, and silently
   *  overwriting an authored field would be a data-loss bug wearing a
   *  convenience feature's clothes. */
  occupied: boolean;
};

/** dnd-kit gives `data` as `Record<string, unknown>`; these narrow it back. */
export function asDragData(data: unknown): BuilderDragData | null {
  const d = data as BuilderDragData | undefined;
  if (d?.kind === 'palette' && d.entry) return d;
  if (d?.kind === 'placedField' && d.sectionId && d.rowId && d.columnId) return d;
  return null;
}

export function asDropData(data: unknown): BuilderDropData | null {
  const d = data as BuilderDropData | undefined;
  if (d?.sectionId && d.rowId && d.columnId) return d;
  return null;
}

/**
 * Pointer travel, in px, before a press becomes a drag.
 *
 * Non-negotiable that this is > 0: every palette card and every column slot is
 * ALSO a click target for arm-and-place. With no activation distance dnd-kit
 * swallows the press and the click path dies — which is precisely the
 * regression that would make this feature a net loss.
 */
export const DRAG_ACTIVATION_DISTANCE_PX = 6;

/** What a completed drag should do. `none` covers every rejected drop. */
export type DropOutcome =
  | { action: 'none' }
  | { action: 'place'; entry: DraggablePaletteEntry; to: BuilderDropData }
  | {
      action: 'move';
      from: { sectionId: string; rowId: string; columnId: string };
      to: BuilderDropData;
    };

/**
 * Decide what a drop means. Pure, so the rules can be tested without a DOM and
 * without dnd-kit — the interesting cases here are all REJECTIONS, and a
 * rejection that silently becomes an accept is data loss.
 */
export function resolveDrop(drag: unknown, drop: unknown): DropOutcome {
  const from = asDragData(drag);
  const to = asDropData(drop);
  // Dropped outside any slot, or the payload wasn't ours.
  if (!from || !to) return { action: 'none' };
  // Occupied slots are never targets: the move endpoint fills an EMPTY column,
  // so accepting here would either fail server-side or overwrite an authored
  // field depending on the endpoint's mood. Neither is acceptable.
  if (to.occupied) return { action: 'none' };

  if (from.kind === 'palette') return { action: 'place', entry: from.entry, to };

  // Self-drop — picked a field up and put it back. Not an error, just nothing.
  if (from.sectionId === to.sectionId && from.rowId === to.rowId && from.columnId === to.columnId) {
    return { action: 'none' };
  }
  return {
    action: 'move',
    from: { sectionId: from.sectionId, rowId: from.rowId, columnId: from.columnId },
    to,
  };
}
